import { Home, User, Scale, Building2, Users, DoorOpen, ShieldCheck, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const DETAILS = [
  { label: "Provision", value: "Standalone, single-occupancy children's home", Icon: Home, color: "#FF508D" },
  { label: "Child", value: "One boy, aged 12-15, with emotional and behavioural difficulties", Icon: User, color: "#FFB019" },
  { label: "Legal framework", value: "Current child-specific court order authorising deprivation of liberty", Icon: Scale, color: "#696DFF" },
  { label: "Home", value: "Three-bedroom terraced house in Aylesbury", Icon: Building2, color: "#5CFFAF" },
  { label: "Staffing", value: "3:1 baseline; capacity to increase to 4:1 when assessed risk or need increases", Icon: Users, color: "#FFB019" },
  { label: "Admission route", value: "Planned referrals and carefully assessed emergency referrals", Icon: DoorOpen, color: "#FF508D" },
];

const ROOMS = ["The child's private bedroom", "The staff sleep-in room", "The home's office"];

const MATCHING = [
  "The Statement of Purpose",
  "The child's current plans",
  "The court order",
  "Known risks",
  "Education and health needs",
  "The home's professional network",
  "The child's views",
  "Information from family and professionals",
];

export default function OurService() {
  return (
    <div data-testid="page-our-service">
      <PageHero
        testid="our-service-hero"
        eyebrow="Our service"
        title={[
          "A home designed",
          <>
            around <em className="text-rainbow italic">one child</em>
          </>,
        ]}
        lead="Infinity & Beyond Care is developing a small, nurturing home in Aylesbury where care is organised around one child's assessed needs, relationships and goals. The home will provide consistent routines, clear boundaries and personalised support within a warm domestic environment."
      />

      {/* Service details */}
      <section className="container-x" data-testid="service-details">
        <StaggerGroup className="border-t border-line">
          {DETAILS.map(({ label, value, Icon, color }) => (
            <StaggerItem key={label}>
              <div className="grid gap-3 border-b border-line py-7 sm:grid-cols-12 sm:items-center sm:gap-8" data-testid={`detail-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-4 sm:col-span-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                  <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">
                    <span className="dot mr-2 !inline-block" style={{ background: color }} aria-hidden="true" />
                    {label}
                  </p>
                </div>
                <p className="text-lg font-bold leading-snug text-ink sm:col-span-8">{value}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Home environment */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="environment-section">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="max-w-3xl">
            <FadeUp>
              <p className="eyebrow">The home environment</p>
              <h2 className="mt-4 font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="environment-title">
                Warm, domestic, and built around ordinary routines
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mute" data-testid="environment-copy">
                The terraced property has three bedrooms: one is the child's private bedroom, one is
                the staff sleep-in room and one is used as the home's office. Shared living, dining
                and kitchen areas support ordinary family-style routines, while the office provides
                secure space for records, planning and management activity.
              </p>
              <p className="mt-4 text-base leading-relaxed text-mute" data-testid="environment-accessibility">
                The property has no specialist physical adaptations; accessibility and environmental
                compatibility will therefore be considered during matching.
              </p>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="environment-rooms">
                {ROOMS.map((room, i) => (
                  <span key={room} className="chip">
                    <span className="dot" style={{ background: ["#FF508D", "#FFB019", "#696DFF"][i] }} />
                    {room}
                  </span>
                ))}
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <div className="overflow-hidden rounded-2xl border border-line shadow-[0_30px_60px_-35px_rgba(23,55,46,0.4)]">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/02b56810-04c0-441d-a60c-72a34e7b1677/images/3d71c542ac78b7ba180de9b6a265e7e5111978686d27d56e95180dff98a2c98c.jpeg"
                alt="The child's gaming and study room at the home"
                className="h-[320px] w-full object-cover sm:h-[420px]"
                data-testid="environment-image"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Admissions and matching */}
      <section className="container-x" data-testid="matching-section">
        <div className="grid gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <p className="eyebrow">Admissions and matching</p>
            <h2 className="mt-4 font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="matching-title">
              Every referral, carefully considered
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-mute" data-testid="matching-copy">
              Every referral will be considered against the home's Statement of Purpose and the
              child's wider world — with the child's own views central to the decision.
            </p>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-7">
            <div className="card h-full p-8 sm:p-10" data-testid="matching-card">
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">
                What informs the decision
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {MATCHING.map((item, i) => (
                  <span key={item} className="chip bg-sand !normal-case !tracking-normal !text-sm !font-bold !px-5 !py-2.5" data-testid={`matching-factor-${i + 1}`}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Legal clarity */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="legal-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-line bg-white p-8 sm:p-12">
            <div className="absolute inset-x-0 top-0 h-1 bg-rainbow" aria-hidden="true" />
            <div className="flex items-start gap-6">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-sand text-ink">
                <ShieldCheck className="h-7 w-7" strokeWidth={1.5} />
              </span>
              <div>
                <h2 className="font-display text-2xl text-ink sm:text-3xl" data-testid="legal-title">Legal clarity</h2>
                <p className="mt-4 max-w-3xl text-lg leading-relaxed text-ink/90" data-testid="legal-copy">
                  This is <strong>not a secure children's home</strong>. Any restriction must be
                  authorised by the child's court order, necessary, proportionate, individually
                  assessed and the least restrictive available.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Contact strip */}
      <section className="container-x" data-testid="contact-strip">
        <FadeUp>
          <div className="flex flex-col items-start gap-6 rounded-3xl bg-sand px-7 py-8 sm:flex-row sm:items-center sm:justify-between sm:px-10">
            <div className="flex flex-col gap-2 text-sm font-bold text-ink sm:flex-row sm:items-center sm:gap-8">
              <span className="flex items-center gap-2" data-testid="strip-address">
                <MapPin className="h-4 w-4" strokeWidth={1.5} /> 81 Bicester Road, Aylesbury, HP19 9AN
              </span>
              <a href="tel:+447748433226" data-testid="strip-phone" className="flex items-center gap-2 transition-colors hover:text-mute">
                <Phone className="h-4 w-4" strokeWidth={1.5} /> +44 7748 433226
              </a>
            </div>
            <Link to="/care-support" data-testid="strip-next-link" className="btn btn-primary !px-5 !py-2.5 text-[13px]">
              How support is delivered
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </FadeUp>
      </section>

      <div className="container-x pb-4">
        <NextPage
          to="/care-support"
          title="Care, staffing and safeguards"
          label="How support is delivered — the staffing model and practice pillars"
          testid="next-care-support"
        />
      </div>
    </div>
  );
}
